import { evaluateFormulaBoolean } from './formula-eval'
import type { Collections, TypeOf, VariableType } from './types'

/**
 * ⛔ ESTE ARCHIVO LO CORREN LOS DOS LADOS, igual que el evaluador de fórmulas.
 *
 * Es el mismo predicado que decide si un bloque condicional se ve en la vista previa del editor y
 * si sale en el correo que recibe una persona. Escrito dos veces —como estaba— las dos copias se
 * separan en la primera corrección, y el síntoma es un párrafo que el redactor vio y el
 * destinatario no.
 *
 * ⚠️ `evaluateExpr` es FAIL-OPEN: un JSON roto o una condición sin cláusulas devuelve `true`, o
 * sea el bloque SE MUESTRA. Es lo correcto para un documento interno y es lo que hace hoy la
 * impresión. Para un correo que sale a un CLIENTE, el llamador tiene que invertirlo: ver
 * `conditionHolds` en el render de correos de Créditos.
 */

// Valores que se consideran "verdadero" (ej. tiene_parqueo === 'SI').
const TRUTHY = new Set(['si', 'sí', 'true', '1', 'x', 'yes', 'y', 'verdadero'])
const isTruthy = (v: string): boolean => TRUTHY.has(v.trim().toLowerCase())

export type ConditionalOperator =
  | 'truthy'
  | 'falsy'
  | 'equals'
  | 'notEquals'
  | 'notEmpty'
  | 'empty'
  | 'gt'
  | 'lt'
  | 'gte'
  | 'lte'
  | 'contains'

export interface ConditionalProps {
  variable: string
  operator: ConditionalOperator
  value?: string
}

/**
 * Alias histórico de `VariableType`.
 *
 * ☠️ Era una COPIA LITERAL de la unión de `types.ts`, dentro del mismo paquete: dos declaraciones
 * del mismo vocabulario a cuatro archivos de distancia, que es como nacen los desacoples que
 * nadie ve (ADR 0036, contexto). Ahora es un alias y no puede separarse. El nombre queda porque lo
 * usa la firma pública `operatorsForType`; el renombre es de otra ola.
 */
export type VariableDataType = VariableType

interface OperatorMeta {
  value: ConditionalOperator
  label: string
  needsValue?: boolean
}

// Catálogo completo de operadores (usado para resúmenes).
export const CONDITIONAL_OPERATORS: OperatorMeta[] = [
  { value: 'truthy', label: 'Es Sí' },
  { value: 'falsy', label: 'Es No' },
  { value: 'equals', label: 'Es igual a', needsValue: true },
  { value: 'notEquals', label: 'Es distinto de', needsValue: true },
  { value: 'gt', label: 'Mayor que', needsValue: true },
  { value: 'lt', label: 'Menor que', needsValue: true },
  { value: 'gte', label: 'Mayor o igual que', needsValue: true },
  { value: 'lte', label: 'Menor o igual que', needsValue: true },
  { value: 'contains', label: 'Contiene', needsValue: true },
  { value: 'notEmpty', label: 'No está vacío' },
  { value: 'empty', label: 'Está vacío' }
]

const byValue = (v: ConditionalOperator): OperatorMeta =>
  CONDITIONAL_OPERATORS.find((o) => o.value === v) as OperatorMeta

/**
 * Operadores válidos según el tipo de dato de la variable. Esto hace el
 * condicional "inteligente": solo ofrece lo que tiene sentido para cada tipo.
 */
export const operatorsForType = (type?: VariableDataType): OperatorMeta[] => {
  const pick = (ops: ConditionalOperator[]): OperatorMeta[] => ops.map(byValue)
  switch (type) {
    case 'boolean':
      return pick(['truthy', 'falsy', 'empty', 'notEmpty'])
    case 'number':
      return pick(['equals', 'notEquals', 'gt', 'lt', 'gte', 'lte', 'empty', 'notEmpty'])
    case 'enum':
      return pick(['equals', 'notEquals', 'empty', 'notEmpty'])
    case 'date':
      return pick(['empty', 'notEmpty'])
    case 'string':
    default:
      return pick(['equals', 'notEquals', 'contains', 'empty', 'notEmpty'])
  }
}

/** Evalúa una condición contra el mapa de variables resueltas. */
export const evaluateCondition = (
  props: ConditionalProps,
  variables: Record<string, string>
): boolean => {
  const raw = variables?.[props.variable]
  const v = (raw ?? '').toString().trim()
  const target = (props.value ?? '').toString().trim().toLowerCase()

  // Para comparaciones numéricas: parsea ambos lados (tolera "1,000.50").
  const numV = parseFloat(v.replace(/,/g, ''))
  const numTarget = parseFloat((props.value ?? '').replace(/,/g, ''))
  const bothNumeric = !isNaN(numV) && !isNaN(numTarget)

  switch (props.operator) {
    case 'truthy':
      return isTruthy(v)
    case 'falsy':
      return !isTruthy(v)
    case 'equals':
      return v.toLowerCase() === target
    case 'notEquals':
      return v.toLowerCase() !== target
    case 'contains':
      return v.toLowerCase().includes(target)
    case 'gt':
      return bothNumeric && numV > numTarget
    case 'lt':
      return bothNumeric && numV < numTarget
    case 'gte':
      return bothNumeric && numV >= numTarget
    case 'lte':
      return bothNumeric && numV <= numTarget
    case 'notEmpty':
      return v.length > 0
    case 'empty':
      return v.length === 0
    default:
      return true
  }
}

// ── Condiciones compuestas (varias cláusulas unidas por Y / O) ──

export interface ConditionClause {
  // 'simple' (default): variable + operador + valor. 'expression': fórmula booleana.
  kind?: 'simple' | 'expression'
  variable: string
  operator: ConditionalOperator
  value?: string
  // Para kind 'expression': la fórmula (ej. meses_entre(fecha_ultima_cuota, fecha_primera_cuota) > 24).
  expr?: string
}

export interface ConditionExpr {
  join: 'and' | 'or'
  clauses: ConditionClause[]
}

/** Parsea el JSON de condición guardado en las props (tolerante a vacío/inválido). */
export const parseExpr = (raw: string | undefined): ConditionExpr => {
  if (!raw) return { join: 'and', clauses: [] }
  try {
    const p = JSON.parse(raw)
    if (p && Array.isArray(p.clauses)) {
      return { join: p.join === 'or' ? 'or' : 'and', clauses: p.clauses }
    }
  } catch {
    /* no es JSON válido → sin condición */
  }
  return { join: 'and', clauses: [] }
}

const isClauseConfigured = (c: ConditionClause): boolean =>
  c?.kind === 'expression' ? Boolean(c.expr?.trim()) : Boolean(c?.variable)

/** Evalúa una expresión compuesta. Sin cláusulas válidas → visible (true). */
export const evaluateExpr = (
  raw: string | undefined,
  variables: Record<string, string>,
  collections?: Collections,
  // Tipo declarado de un token. Lo provee el HOST: el editor de documentos pasa el del esquema
  // activo, el correo de Créditos el suyo. Sin él, todo se compara como texto.
  typeOf?: TypeOf
): boolean => evaluateParsedExpr(parseExpr(raw), variables, collections, typeOf)

/**
 * La misma evaluación, sobre una expresión YA parseada.
 *
 * Existe para quien ya tuvo que mirar adentro de la condición antes de evaluarla —el «Mostrar sólo
 * si» de un campo, que primero comprueba que sus variables existan— y así no la parsea dos veces.
 * ⛔ El predicado vive acá y en ningún otro lado: `evaluateExpr` es esta función con un `parseExpr`
 * adelante, no una segunda implementación.
 */
export const evaluateParsedExpr = (
  expr: ConditionExpr,
  variables: Record<string, string>,
  collections?: Collections,
  typeOf?: TypeOf
): boolean => {
  const { join, clauses } = expr ?? { join: 'and', clauses: [] }
  const valid = (clauses ?? []).filter(isClauseConfigured)
  if (valid.length === 0) return true
  const results = valid.map((c) =>
    c.kind === 'expression'
      ? evaluateFormulaBoolean(c.expr ?? '', variables, collections, typeOf)
      : evaluateCondition(c, variables)
  )
  return join === 'or' ? results.some(Boolean) : results.every(Boolean)
}
